import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react'
import { Layout, Typography, Row, Col, Select, Button, Space, Alert, Card, Segmented, message } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { Line } from '@ant-design/charts'
import moment from 'moment-timezone'
import useQueuesStore from '../../stores/useQueuesStore'
import QueueCard from './QueueCard'
import JobsTable from './JobsTable'
import JobDetail from './JobDetail'

const { Content } = Layout
const { Title, Text } = Typography
const { Option } = Select

const STATUSES = ['waiting', 'active', 'completed', 'failed', 'delayed']
const POLL_INTERVAL = 10000
const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'

const Queues = () => {
  const {
    queues,
    jobs,
    stats,
    selectedQueue,
    selectedStatus,
    pagination,
    jobsLoading,
    fetchQueues,
    fetchStats,
    fetchJobs,
    getJob,
    retryJob,
    retryAllFailed,
  } = useQueuesStore()

  const [detailJob, setDetailJob] = useState(null)
  const [detailVisible, setDetailVisible] = useState(false)
  const [timezone, setTimezone] = useState(LOCAL_TZ)
  const [use24h, setUse24h] = useState(true)
  const [messageApi, contextHolder] = message.useMessage()
  const pollRef = useRef(null)

  const timeZones = useMemo(() => {
    return moment.tz.names().map(tz => {
      const offset = moment.tz(tz).format('Z')
      return { label: `(GMT${offset}) ${tz}`, value: tz }
    })
  }, [])

  useEffect(() => {
    fetchQueues()
    fetchStats()
    pollRef.current = setInterval(() => {
      fetchQueues()
      fetchStats()
    }, POLL_INTERVAL)
    return () => clearInterval(pollRef.current)
  }, [fetchQueues, fetchStats])

  // Auto-select the first queue on initial load
  useEffect(() => {
    if (queues.length > 0 && !selectedQueue) {
      fetchJobs(queues[0].name, 'completed', 1)
    }
  }, [queues, selectedQueue, fetchJobs])

  const handleQueueClick = useCallback((name) => {
    fetchJobs(name, selectedStatus, 1)
  }, [fetchJobs, selectedStatus])

  const handleStatusChange = useCallback((status) => {
    if (selectedQueue) {
      fetchJobs(selectedQueue, status, 1)
    }
  }, [fetchJobs, selectedQueue])

  const handlePageChange = useCallback((page) => {
    if (selectedQueue) {
      fetchJobs(selectedQueue, selectedStatus, page)
    }
  }, [fetchJobs, selectedQueue, selectedStatus])

  const handleRetryJob = useCallback(async (jobId) => {
    try {
      await retryJob(selectedQueue, jobId)
      messageApi.success(`Job ${jobId} queued for retry`)
    } catch (err) {
      messageApi.error(err.message || 'Retry failed')
    }
  }, [retryJob, selectedQueue, messageApi])

  const handleRetryAll = useCallback(async () => {
    try {
      const count = await retryAllFailed(selectedQueue)
      messageApi.success(`${count} jobs queued for retry`)
    } catch (err) {
      messageApi.error(err.message || 'Retry all failed')
    }
  }, [retryAllFailed, selectedQueue, messageApi])

  const handleRowClick = useCallback(async (record) => {
    try {
      const fullJob = await getJob(selectedQueue, record.id)
      setDetailJob(fullJob)
      setDetailVisible(true)
    } catch (err) {
      messageApi.error('Failed to load job details')
    }
  }, [getJob, selectedQueue, messageApi])

  const handleRefresh = useCallback(() => {
    fetchQueues()
    fetchStats()
    if (selectedQueue) {
      fetchJobs(selectedQueue, selectedStatus, pagination.page)
    }
  }, [fetchQueues, fetchStats, fetchJobs, selectedQueue, selectedStatus, pagination.page])

  // Build chart data — convert UTC hours to selected timezone
  const chartData = useMemo(() => {
    return stats.flatMap((s) => {
      const localHour = moment.utc(s.hour).tz(timezone).format(use24h ? 'MM-DD HH:mm' : 'MM-DD h A')
      return [
        { hour: localHour, queue: s.queue, type: 'Completed', value: s.completed },
        ...(s.failed > 0 ? [{ hour: localHour, queue: s.queue, type: 'Failed', value: s.failed }] : []),
      ]
    }).filter((d) => !selectedQueue || d.queue === selectedQueue)
  }, [stats, timezone, use24h, selectedQueue])

  const lineConfig = {
    data: chartData,
    xField: 'hour',
    yField: 'value',
    colorField: 'type',
    shapeField: 'smooth',
    scale: {
      color: { range: ['#52c41a', '#ff4d4f'] },
      y: { nice: true, tickCount: 5 },
    },
    axis: {
      x: { labelFontSize: 10, labelFill: '#999', labelAutoRotate: true },
      y: {
        labelFontSize: 10,
        labelFill: '#999',
        labelFormatter: (v) => Number.isInteger(v) ? String(v) : '',
        grid: true,
        gridStroke: '#f0f0f0',
      },
    },
    tooltip: (d) => ({ name: d.type, value: d.value }),
    height: 200,
    autoFit: true,
  }

  return (
    <Content style={{ padding: 24 }}>
      {contextHolder}

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>Scheduler</Title>
        </Col>
        <Col>
          <Space>
            <Select
              showSearch
              value={timezone}
              onChange={setTimezone}
              style={{ minWidth: 260 }}
              size="small"
              placeholder="Select timezone"
              optionFilterProp="children"
            >
              {timeZones.map((tz) => (
                <Option key={tz.value} value={tz.value}>{tz.label}</Option>
              ))}
            </Select>
            <Segmented
              size="small"
              value={use24h ? '24h' : '12h'}
              options={['12h', '24h']}
              onChange={(val) => setUse24h(val === '24h')}
            />
            <Button icon={<ReloadOutlined />} onClick={handleRefresh} size="small">
              Refresh
            </Button>
          </Space>
        </Col>
      </Row>

      <Alert
        type="info"
        showIcon
        message="Job history is stored in Redis and retained for 7 days. Data does not persist across Redis restarts."
        style={{ marginBottom: 16 }}
      />

      <Row gutter={12} style={{ marginBottom: 16 }}>
        {queues.map((q) => (
          <Col span={8} key={q.name}>
            <QueueCard
              queue={q}
              selected={selectedQueue === q.name}
              onClick={() => handleQueueClick(q.name)}
            />
          </Col>
        ))}
      </Row>

      <Card
        size="small"
        title={<Text strong style={{ fontSize: 13 }}>
          Throughput (48h){selectedQueue ? ` — ${selectedQueue}` : ''}
        </Text>}
        style={{ marginBottom: 16 }}
      >
        {chartData.length > 0
          ? <Line {...lineConfig} />
          : <Text type="secondary" style={{ display: 'block', textAlign: 'center', padding: 32 }}>No job activity in the last 48 hours</Text>
        }
      </Card>

      {selectedQueue && (
        <>
          <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
            <Col>
              <Space>
                <Text strong>Status:</Text>
                <Select
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  style={{ width: 140 }}
                  size="small"
                  options={STATUSES.map((s) => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
                />
              </Space>
            </Col>
            <Col>
              {selectedStatus === 'failed' && (
                <Button type="primary" danger size="small" onClick={handleRetryAll}>
                  Retry All Failed
                </Button>
              )}
            </Col>
          </Row>

          <JobsTable
            jobs={jobs}
            loading={jobsLoading}
            pagination={pagination}
            status={selectedStatus}
            timezone={timezone}
            use24h={use24h}
            onPageChange={handlePageChange}
            onRetry={handleRetryJob}
            onRowClick={handleRowClick}
          />
        </>
      )}

      <JobDetail
        job={detailJob}
        visible={detailVisible}
        timezone={timezone}
        use24h={use24h}
        onClose={() => setDetailVisible(false)}
        onRetry={handleRetryJob}
      />
    </Content>
  )
}

export default Queues
